import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  MessageSquare, BookOpen, Zap, Trash2, Eye, X, Plus, Upload,
  Globe, Loader2, AlertCircle, CheckCircle2, ToggleLeft, ToggleRight,
  Settings, FileText, ChevronLeft, ChevronRight, Send,
} from 'lucide-react'
import {
  fetchConversations, fetchConversation, deleteConversation, closeConversation,
  fetchKbArticles, createKbArticle, updateKbArticle, deleteKbArticle, fetchUrlContent,
  fetchKbFiles, uploadKbFile, deleteKbFile,
  fetchPipelines, togglePipeline, updatePipelineConfig, fetchPipelineRuns, triggerOktadesk,
  type ConversationListItem, type ConversationDetail, type KbArticle,
  type KbFile, type Pipeline, type PipelineRun,
} from '@/lib/supportAdminApi'

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function fmtDate(iso: string) {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    open: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
    closed: 'bg-muted text-muted-foreground border-border',
    ticket_created: 'bg-amber-500/15 text-amber-600 border-amber-500/30',
  }
  const label: Record<string, string> = {
    open: 'Aberta', closed: 'Fechada', ticket_created: 'Ticket criado',
  }
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${map[status] ?? map.open}`}>
      {label[status] ?? status}
    </span>
  )
}

function RunStatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    success: 'bg-emerald-500/15 text-emerald-600',
    failed: 'bg-destructive/15 text-destructive',
    pending: 'bg-amber-500/15 text-amber-600',
  }
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${map[status] ?? ''}`}>
      {status}
    </span>
  )
}

// ─── Conversations Tab ────────────────────────────────────────────────────────

export function ConversasTab() {
  const [data, setData] = useState<{ items: ConversationListItem[]; total: number; page: number; per_page: number } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [page, setPage] = useState(1)
  const [filterStatus, setFilterStatus] = useState('')
  const [filterEmail, setFilterEmail] = useState('')
  const [selected, setSelected] = useState<ConversationDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await fetchConversations({ page, status: filterStatus || undefined, email: filterEmail || undefined })
      setData(res)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar conversas')
    } finally {
      setLoading(false)
    }
  }, [page, filterStatus, filterEmail])

  useEffect(() => { load() }, [load])

  const openDetail = async (id: string) => {
    setDetailLoading(true)
    try {
      const detail = await fetchConversation(id)
      setSelected(detail)
    } catch { /* ignore */ }
    finally { setDetailLoading(false) }
  }

  const handleClose = async (id: string) => {
    await closeConversation(id)
    load()
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Deletar esta conversa?')) return
    await deleteConversation(id)
    load()
  }

  const handleTriggerTicket = async (id: string) => {
    try {
      const res = await triggerOktadesk(id)
      if (res.success) {
        alert(`Ticket criado! ID: ${res.ticket_id ?? res.run_id}`)
        load()
      } else {
        alert('Falha ao criar ticket. Verifique a configuração do Oktadesk.')
      }
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Erro ao criar ticket')
    }
  }

  const totalPages = data ? Math.ceil(data.total / data.per_page) : 1

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <select
          value={filterStatus}
          onChange={e => { setFilterStatus(e.target.value); setPage(1) }}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="">Todos os status</option>
          <option value="open">Aberta</option>
          <option value="closed">Fechada</option>
          <option value="ticket_created">Ticket criado</option>
        </select>
        <Input
          placeholder="Filtrar por e-mail..."
          value={filterEmail}
          onChange={e => { setFilterEmail(e.target.value); setPage(1) }}
          className="h-9 w-56 text-sm"
        />
        <Button size="sm" variant="outline" onClick={load}>Atualizar</Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-destructive">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>E-mail</TableHead>
                <TableHead>1ª mensagem</TableHead>
                <TableHead className="text-center">Msgs</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    Nenhuma conversa encontrada.
                  </TableCell>
                </TableRow>
              )}
              {data?.items.map(conv => (
                <TableRow key={conv.id}>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{fmtDate(conv.created_at)}</TableCell>
                  <TableCell className="text-sm">{conv.user_email ?? <span className="text-muted-foreground italic">Anônimo</span>}</TableCell>
                  <TableCell className="text-sm max-w-[200px] truncate">{conv.first_message ?? '—'}</TableCell>
                  <TableCell className="text-center text-sm">{conv.message_count}</TableCell>
                  <TableCell><StatusBadge status={conv.status} /></TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openDetail(conv.id)} title="Ver conversa">
                        {detailLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
                      </Button>
                      {conv.status === 'open' && (
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleTriggerTicket(conv.id)} title="Abrir ticket no Oktadesk">
                          <Send className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {conv.status === 'open' && (
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleClose(conv.id)} title="Fechar conversa">
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDelete(conv.id)} title="Deletar">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>{data?.total} conversas</span>
              <div className="flex items-center gap-2">
                <Button size="icon" variant="outline" className="h-7 w-7" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                  <ChevronLeft className="h-3.5 w-3.5" />
                </Button>
                <span>Página {page} de {totalPages}</span>
                <Button size="icon" variant="outline" className="h-7 w-7" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Conversation Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={open => !open && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Conversa</DialogTitle>
            <DialogDescription>
              {selected?.user_email ?? 'Usuário anônimo'} · {selected && fmtDate(selected.created_at)} · <StatusBadge status={selected?.status ?? 'open'} />
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-3 py-2 min-h-0">
            {selected?.messages.map(msg => (
              <div key={msg.id} className={`flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                <div className={`max-w-[80%] rounded-xl px-3 py-2 text-sm whitespace-pre-wrap ${
                  msg.role === 'user'
                    ? 'bg-primary text-primary-foreground rounded-br-none'
                    : 'bg-muted text-foreground rounded-bl-none'
                }`}>
                  {msg.content}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ─── KB Tab ───────────────────────────────────────────────────────────────────

export function KbTab() {
  const [subTab, setSubTab] = useState<'articles' | 'files'>('articles')
  const [articles, setArticles] = useState<KbArticle[]>([])
  const [files, setFiles] = useState<KbFile[]>([])
  const [loading, setLoading] = useState(true)

  // Article form
  const [articleDialog, setArticleDialog] = useState(false)
  const [editingArticle, setEditingArticle] = useState<KbArticle | null>(null)
  const [artTitle, setArtTitle] = useState('')
  const [artContent, setArtContent] = useState('')
  const [artUrl, setArtUrl] = useState('')
  const [artSaving, setArtSaving] = useState(false)

  // URL import
  const [urlInput, setUrlInput] = useState('')
  const [urlLoading, setUrlLoading] = useState(false)
  const [urlError, setUrlError] = useState('')

  // File upload
  const fileRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  const loadAll = useCallback(async () => {
    setLoading(true)
    try {
      const [arts, fls] = await Promise.all([fetchKbArticles(), fetchKbFiles()])
      setArticles(arts)
      setFiles(fls)
    } catch { /* ignore */ }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { loadAll() }, [loadAll])

  const openNew = () => {
    setEditingArticle(null)
    setArtTitle('')
    setArtContent('')
    setArtUrl('')
    setUrlInput('')
    setUrlError('')
    setArticleDialog(true)
  }

  const openEdit = (a: KbArticle) => {
    setEditingArticle(a)
    setArtTitle(a.title)
    setArtContent(a.content)
    setArtUrl(a.source_url ?? '')
    setUrlInput('')
    setUrlError('')
    setArticleDialog(true)
  }

  const handleFetchUrl = async () => {
    if (!urlInput.trim()) return
    setUrlLoading(true)
    setUrlError('')
    try {
      const res = await fetchUrlContent(urlInput.trim())
      setArtTitle(res.title)
      setArtContent(res.content)
      setArtUrl(urlInput.trim())
    } catch (e: unknown) {
      setUrlError(e instanceof Error ? e.message : 'Não foi possível buscar a URL')
    } finally {
      setUrlLoading(false)
    }
  }

  const saveArticle = async () => {
    if (!artTitle.trim() || !artContent.trim()) return
    setArtSaving(true)
    try {
      if (editingArticle) {
        await updateKbArticle(editingArticle.id, { title: artTitle, content: artContent, source_url: artUrl || undefined })
      } else {
        await createKbArticle({ title: artTitle, content: artContent, source_url: artUrl || undefined })
      }
      setArticleDialog(false)
      loadAll()
    } catch { /* ignore */ }
    finally { setArtSaving(false) }
  }

  const handleDeleteArticle = async (id: string) => {
    if (!confirm('Deletar este artigo?')) return
    await deleteKbArticle(id)
    loadAll()
  }

  const handleUploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      await uploadKbFile(file)
      loadAll()
    } catch { /* ignore */ }
    finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const handleDeleteFile = async (id: string) => {
    if (!confirm('Deletar este arquivo?')) return
    await deleteKbFile(id)
    loadAll()
  }

  if (loading) return (
    <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
  )

  return (
    <div className="space-y-4">
      <div className="flex gap-2 border-b pb-2">
        <Button variant={subTab === 'articles' ? 'default' : 'ghost'} size="sm" onClick={() => setSubTab('articles')}>
          <FileText className="h-3.5 w-3.5 mr-1.5" />Artigos ({articles.length})
        </Button>
        <Button variant={subTab === 'files' ? 'default' : 'ghost'} size="sm" onClick={() => setSubTab('files')}>
          <Upload className="h-3.5 w-3.5 mr-1.5" />Arquivos ({files.length})
        </Button>
      </div>

      {subTab === 'articles' && (
        <>
          <div className="flex justify-end">
            <Button size="sm" onClick={openNew}>
              <Plus className="h-4 w-4 mr-1.5" />Novo Artigo
            </Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Título</TableHead>
                <TableHead>Fonte</TableHead>
                <TableHead>Atualizado</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {articles.length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">Nenhum artigo cadastrado.</TableCell></TableRow>
              )}
              {articles.map(a => (
                <TableRow key={a.id}>
                  <TableCell className="text-sm font-medium">{a.title}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {a.source_url ? (
                      <a href={a.source_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:underline">
                        <Globe className="h-3 w-3" />URL
                      </a>
                    ) : 'Manual'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{fmtDate(a.updated_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(a)}><Eye className="h-3.5 w-3.5" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDeleteArticle(a.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      {subTab === 'files' && (
        <>
          <div className="flex justify-end">
            <Button size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
              Upload PDF / DOCX
            </Button>
            <input ref={fileRef} type="file" accept=".pdf,.docx" className="hidden" onChange={handleUploadFile} />
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Arquivo</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Conteúdo</TableHead>
                <TableHead>Data</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {files.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Nenhum arquivo enviado.</TableCell></TableRow>
              )}
              {files.map(f => (
                <TableRow key={f.id}>
                  <TableCell className="text-sm font-medium">{f.file_name}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs uppercase">{f.file_type ?? '?'}</Badge></TableCell>
                  <TableCell>
                    {f.has_content
                      ? <span className="flex items-center gap-1 text-xs text-emerald-600"><CheckCircle2 className="h-3 w-3" />Extraído</span>
                      : <span className="flex items-center gap-1 text-xs text-muted-foreground"><AlertCircle className="h-3 w-3" />Sem conteúdo</span>}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{fmtDate(f.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDeleteFile(f.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      {/* Article Dialog */}
      <Dialog open={articleDialog} onOpenChange={setArticleDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingArticle ? 'Editar Artigo' : 'Novo Artigo'}</DialogTitle>
            <DialogDescription>Conteúdo que será usado como contexto pela IA no chat.</DialogDescription>
          </DialogHeader>

          {/* URL import */}
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Importar de URL (opcional)</Label>
            <div className="flex gap-2">
              <Input
                placeholder="https://..."
                value={urlInput}
                onChange={e => setUrlInput(e.target.value)}
                className="text-sm"
              />
              <Button size="sm" variant="outline" onClick={handleFetchUrl} disabled={urlLoading}>
                {urlLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Globe className="h-3.5 w-3.5" />}
              </Button>
            </div>
            {urlError && <p className="text-xs text-destructive">{urlError}</p>}
          </div>

          <div className="space-y-1">
            <Label>Título</Label>
            <Input value={artTitle} onChange={e => setArtTitle(e.target.value)} placeholder="Ex: Como acessar o curso" />
          </div>
          <div className="space-y-1">
            <Label>Conteúdo</Label>
            <Textarea
              value={artContent}
              onChange={e => setArtContent(e.target.value)}
              placeholder="Escreva o conteúdo do artigo em Markdown..."
              rows={10}
              className="text-sm font-mono resize-none"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setArticleDialog(false)}>Cancelar</Button>
            <Button onClick={saveArticle} disabled={artSaving || !artTitle.trim() || !artContent.trim()}>
              {artSaving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ─── Pipelines Tab ────────────────────────────────────────────────────────────

export function PipelinesTab() {
  const [pipelines, setPipelines] = useState<Pipeline[]>([])
  const [runs, setRuns] = useState<PipelineRun[]>([])
  const [loading, setLoading] = useState(true)
  const [configDialog, setConfigDialog] = useState<Pipeline | null>(null)
  const [configFields, setConfigFields] = useState<Record<string, string>>({})
  const [configSaving, setConfigSaving] = useState(false)
  const [toggling, setToggling] = useState<string | null>(null)
  const [selectedPipeline, setSelectedPipeline] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const ps = await fetchPipelines()
      setPipelines(ps)
      if (ps.length > 0) {
        const name = selectedPipeline ?? ps[0].name
        setSelectedPipeline(name)
        const rs = await fetchPipelineRuns(name)
        setRuns(rs)
      }
    } catch { /* ignore */ }
    finally { setLoading(false) }
  }, [selectedPipeline])

  useEffect(() => { load() }, [load])

  const handleToggle = async (name: string) => {
    setToggling(name)
    try {
      const updated = await togglePipeline(name)
      setPipelines(prev => prev.map(p => p.name === name ? updated : p))
    } catch { /* ignore */ }
    finally { setToggling(null) }
  }

  const openConfig = (p: Pipeline) => {
    setConfigDialog(p)
    setConfigFields({ ...p.config })
  }

  const saveConfig = async () => {
    if (!configDialog) return
    setConfigSaving(true)
    try {
      const updated = await updatePipelineConfig(configDialog.name, configFields)
      setPipelines(prev => prev.map(p => p.name === configDialog.name ? updated : p))
      setConfigDialog(null)
    } catch { /* ignore */ }
    finally { setConfigSaving(false) }
  }

  const loadRuns = async (name: string) => {
    setSelectedPipeline(name)
    try {
      const rs = await fetchPipelineRuns(name)
      setRuns(rs)
    } catch { /* ignore */ }
  }

  if (loading) return (
    <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
  )

  const PIPELINE_FIELDS: Record<string, { key: string; label: string; type: string }[]> = {
    oktadesk: [
      { key: 'subdomain', label: 'Subdomínio', type: 'text' },
      { key: 'api_key', label: 'API Key', type: 'password' },
    ],
  }

  return (
    <div className="space-y-6">
      {/* Pipeline cards */}
      <div className="grid gap-4 sm:grid-cols-2">
        {pipelines.map(p => (
          <Card key={p.id} className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Zap className="h-4 w-4 text-primary shrink-0" />
                  <h3 className="font-semibold text-sm">{p.display_name}</h3>
                </div>
                {p.description && <p className="text-xs text-muted-foreground">{p.description}</p>}
              </div>
              <button
                onClick={() => handleToggle(p.name)}
                disabled={toggling === p.name}
                className="shrink-0 mt-0.5"
                title={p.is_enabled ? 'Desativar' : 'Ativar'}
              >
                {toggling === p.name
                  ? <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  : p.is_enabled
                    ? <ToggleRight className="h-6 w-6 text-primary" />
                    : <ToggleLeft className="h-6 w-6 text-muted-foreground" />}
              </button>
            </div>
            <div className="mt-4 flex gap-2">
              <Badge
                variant="outline"
                className={p.is_enabled
                  ? 'border-emerald-500/30 text-emerald-600 bg-emerald-500/10'
                  : 'text-muted-foreground'}
              >
                {p.is_enabled ? 'Ativo' : 'Inativo'}
              </Badge>
              <Button
                size="sm"
                variant="outline"
                className="h-6 text-xs px-2"
                onClick={() => openConfig(p)}
              >
                <Settings className="h-3 w-3 mr-1" />Configurar
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-6 text-xs px-2"
                onClick={() => loadRuns(p.name)}
              >
                Ver histórico
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {/* Runs table */}
      {selectedPipeline && (
        <div>
          <h3 className="text-sm font-medium mb-3">
            Últimas execuções — {pipelines.find(p => p.name === selectedPipeline)?.display_name}
          </h3>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Pergunta</TableHead>
                <TableHead>Tokens · Custo · Steps</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">Nenhuma execução registrada.</TableCell></TableRow>
              )}
              {runs.map(r => {
                const rd = r.result_data as {
                  total_tokens?: number
                  cost_usd?: number
                  thinking_steps?: unknown[]
                  is_unanswered?: boolean
                  chunks?: number
                } | null
                const td = r.trigger_data as { message?: string; has_email?: boolean } | null

                return (
                  <TableRow key={r.id}>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{fmtDate(r.created_at)}</TableCell>
                    <TableCell><RunStatusBadge status={r.status} /></TableCell>
                    <TableCell className="text-xs max-w-[200px]">
                      <span className="block truncate text-foreground">{td?.message ?? r.conversation_id?.slice(0, 8) ?? '—'}</span>
                      {td?.has_email && <span className="text-primary/60 text-[10px]">com e-mail</span>}
                    </TableCell>
                    <TableCell className="text-xs">
                      {r.error_message ? (
                        <span className="text-destructive">{r.error_message.slice(0, 60)}</span>
                      ) : rd ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="text-muted-foreground">
                            {[
                              rd.total_tokens != null ? `${rd.total_tokens} tok` : null,
                              rd.cost_usd != null ? `$${rd.cost_usd.toFixed(5)}` : null,
                              Array.isArray(rd.thinking_steps) ? `${rd.thinking_steps.length} steps` : null,
                            ].filter(Boolean).join(' · ')}
                          </span>
                          {rd.is_unanswered && (
                            <span className="text-amber-600 font-medium text-[10px]">Não respondida</span>
                          )}
                        </div>
                      ) : '—'}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Config Dialog */}
      <Dialog open={!!configDialog} onOpenChange={open => !open && setConfigDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Configurar {configDialog?.display_name}</DialogTitle>
            <DialogDescription>As chaves ficam salvas no banco de dados (criptografadas em produção).</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {(PIPELINE_FIELDS[configDialog?.name ?? ''] ?? []).map(field => (
              <div key={field.key} className="space-y-1">
                <Label>{field.label}</Label>
                <Input
                  type={field.type}
                  value={configFields[field.key] ?? ''}
                  onChange={e => setConfigFields(prev => ({ ...prev, [field.key]: e.target.value }))}
                  placeholder={field.type === 'password' ? '••••••••' : field.label}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigDialog(null)}>Cancelar</Button>
            <Button onClick={saveConfig} disabled={configSaving}>
              {configSaving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function AdminSuporte() {
  const { user, loading } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = searchParams.get('tab') || 'conversas'

  useEffect(() => {
    if (loading) return
    if (!user) { navigate('/auth', { replace: true }); return }
  }, [user, loading, navigate])

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  )

  if (!user) return null

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Suporte IA</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Gerencie conversas, base de conhecimento e integrações da Central de Ajuda.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setSearchParams({ tab: v })}>
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="conversas" className="gap-1.5">
            <MessageSquare className="h-3.5 w-3.5" />Conversas
          </TabsTrigger>
          <TabsTrigger value="kb" className="gap-1.5">
            <BookOpen className="h-3.5 w-3.5" />Base de Conhecimento
          </TabsTrigger>
          <TabsTrigger value="pipelines" className="gap-1.5">
            <Zap className="h-3.5 w-3.5" />Pipelines
          </TabsTrigger>
        </TabsList>

        <Card className="mt-4 p-6">
          <TabsContent value="conversas" className="mt-0">
            <ConversasTab />
          </TabsContent>
          <TabsContent value="kb" className="mt-0">
            <KbTab />
          </TabsContent>
          <TabsContent value="pipelines" className="mt-0">
            <PipelinesTab />
          </TabsContent>
        </Card>
      </Tabs>
    </div>
  )
}
